/**
 * `DimeSessionCache` — the provider-held auth/connection cache that makes a
 * terminal mount cost ONE platform mint and a remount cost ZERO.
 *
 * Why it exists: every DIME JWT is minted through the host's exchange, and on
 * native-auth hosts each mint is a real backend call (for Paradex a paid,
 * rate-limited `kms:Sign`). Historically each mint site cached privately and
 * per-mount, so a single mount cost ~4 mints and ordinary host-chrome
 * interactions (drawer pin/unpin, drawer ↔ fullscreen) re-minted everything —
 * enough to trip Paradex's 10/min-per-account limit. This cache is created
 * once by `DimeTerminalProvider` (which survives those remounts) and shared
 * by every mint site below it.
 *
 * Contents (all memory-only — NOTHING here may ever be persisted):
 *  - one DIME identity per key, reused while it has meaningful life left,
 *    with concurrent callers single-flighted onto one in-flight mint;
 *  - one connection grant per key, mirroring the server's own ~5-minute
 *    grant-cache staleness bound.
 *
 * Keys are `(apiUrl, accountId)` so identities can never be reused across
 * accounts or environments. Failures are never cached. Invalidate on: 401
 * (stale/revoked JWT), access-code redeem (the cached identity's `onboarded`
 * is stale), unlink, sign-out, account switch (keyed miss).
 */
import { decodeJwtPayload, type DimeIdentity } from './identity.js';
import type { ConnectionGrant } from './api.js';

/** Re-mint when the cached identity has less life left than this. */
const IDENTITY_ASYNC_SLACK_MS = 60_000;
/** Sync consumers (sidecar headers) tolerate a tighter margin. */
const IDENTITY_PEEK_SLACK_MS = 30_000;
/** Fallback lifetime when a JWT unexpectedly carries no `exp`. */
const IDENTITY_FALLBACK_TTL_MS = 4 * 60_000;
/** Grant reuse bound — mirrors the web-api's own grant-cache TTL. */
const GRANT_TTL_MS = 5 * 60_000;

/** Build the cache key for one (apiUrl, accountId) session. */
export function sessionCacheKey(apiUrl: string, accountId: string): string {
  return `${apiUrl}\u001f${accountId}`;
}

export interface DimeSessionCache {
  /**
   * The cached identity for `key`, minting via `mint` when absent or within
   * 60s of expiry. Concurrent callers share one in-flight mint; a rejected
   * mint is never cached (every caller sees the rejection, the next call
   * re-mints).
   */
  readonly getIdentity: (
    key: string,
    mint: () => Promise<DimeIdentity>,
  ) => Promise<DimeIdentity>;
  /**
   * Synchronous read for consumers that cannot await (sidecar headers):
   * the cached token while it has >30s left, else null. Never mints.
   */
  readonly peekToken: (key: string) => string | null;
  readonly getGrant: (key: string) => ConnectionGrant | null;
  readonly setGrant: (key: string, grant: ConnectionGrant) => void;
  /** Drop the identity for `key` (401 / redeem — grant may still be good). */
  readonly invalidateIdentity: (key: string) => void;
  /** Drop the grant for `key` (dead connection / non-connected resolution). */
  readonly invalidateGrant: (key: string) => void;
  /** Drop everything (unlink, sign-out — the session is over). */
  readonly clear: () => void;
}

interface IdentityEntry {
  readonly identity: DimeIdentity;
  readonly expiresAtMs: number;
}

interface GrantEntry {
  readonly grant: ConnectionGrant;
  readonly expiresAtMs: number;
}

export function createDimeSessionCache(): DimeSessionCache {
  const identities = new Map<string, IdentityEntry>();
  const inFlight = new Map<string, Promise<DimeIdentity>>();
  const grants = new Map<string, GrantEntry>();
  // Bumped by clear(): an in-flight mint from a cleared session must not
  // re-insert its identity when it settles (the session is over).
  let generation = 0;

  const identityExpiry = (identity: DimeIdentity): number => {
    const payload = decodeJwtPayload(identity.token);
    const exp = typeof payload?.exp === 'number' ? payload.exp : null;
    return exp != null ? exp * 1000 : Date.now() + IDENTITY_FALLBACK_TTL_MS;
  };

  return {
    getIdentity: (key, mint) => {
      const cached = identities.get(key);
      if (
        cached != null &&
        Date.now() < cached.expiresAtMs - IDENTITY_ASYNC_SLACK_MS
      ) {
        return Promise.resolve(cached.identity);
      }
      const pending = inFlight.get(key);
      if (pending != null) return pending;
      const startedGeneration = generation;
      const attempt = mint()
        .then((identity) => {
          if (generation === startedGeneration) {
            identities.set(key, {
              identity,
              expiresAtMs: identityExpiry(identity),
            });
          }
          return identity;
        })
        .finally(() => {
          if (inFlight.get(key) === attempt) inFlight.delete(key);
        });
      inFlight.set(key, attempt);
      return attempt;
    },
    peekToken: (key) => {
      const cached = identities.get(key);
      if (
        cached == null ||
        Date.now() >= cached.expiresAtMs - IDENTITY_PEEK_SLACK_MS
      ) {
        return null;
      }
      return cached.identity.token;
    },
    getGrant: (key) => {
      const cached = grants.get(key);
      if (cached == null || Date.now() >= cached.expiresAtMs) return null;
      return cached.grant;
    },
    setGrant: (key, grant) => {
      grants.set(key, { grant, expiresAtMs: Date.now() + GRANT_TTL_MS });
    },
    invalidateIdentity: (key) => {
      identities.delete(key);
    },
    invalidateGrant: (key) => {
      grants.delete(key);
    },
    clear: () => {
      generation += 1; // in-flight mints from the cleared session won't store
      identities.clear();
      grants.clear();
      for (const [key, pending] of inFlight) {
        inFlight.delete(key);
        void pending.catch(() => {}); // detach; avoid unhandled rejections
      }
    },
  };
}
